
import { Device } from "@capacitor/device";
import { Component, Provide, Vue, Watch } from "vue-property-decorator";
import MessageInfoService from "@/services/message-info-service";
import SessionTimeoutInfo from "@/models/session-timeout-info";
import { format, isBefore, parseISO } from "date-fns";
import Login from "@/components/Login.vue";
import ActionsList from "@/components/ActionsList.vue";
import ToastComponent from "@/components/toast-component.vue";
import { ILoginInfo } from "@/models/login-info";

Vue.filter("formatDate", (value: any) => {
  if (value) {
    return format(parseISO(value), "MM yyyy");
  }
});

@Component({
  components: { ToastComponent, ActionsList, Login }
})
export default class App extends Vue {
  drawer: null | boolean = null;
  miniMenu = false;
  lightTheme = false;
  showProgress = false;
  get isUserLogged() {
    return this.$dataService.getters.Auth.isUserLogged();
  }
  loginDialog = false;
  $vuetify: any;
  vm: any;
  buildVersion = process.env.VUE_APP_BUILD_VERSION || "DEBUG";
  swSnackBarMode = false;
  swUpdatedFoundText = "Found New Web App Version! Downloading...";
  swUpdatedText = "Success! New Version is Installed! Refreshing...";
  swText = "";
  userInfo: ILoginInfo | null = null;
  showNotificationRunAllDialog = false;
  sendMsgDialog = false;
  subjectMessage: string | null = null;
  commentsMessage: string | null = null;
  formValid = false;
  messageSending = false;
  elapsedTimeProgress: number = 0;
  elapsedTimeDisplay: string = "";
  deferredPrompt: any | null = null;
  showInstallAppButton = false;
  isInitialize = false;
  showUpdateMsg = false;
  VUE_APP_INSTALLBUTTON: string = process.env.VUE_APP_INSTALLBUTTON ?? "DISABLE";
  VUE_APP_SHOW_VOP: string = process.env.VUE_APP_SHOW_VOP ?? "NO";

  $refs!: {
    form: HTMLFormElement;
  };

  get isShipAdmin() {
    return this.$dataService.actions.Auth.getShipAdmin().length > 0;
  }
  get offLineStatus() {
    return this.$dataService.getters.isOffline();
  }
  get offlineSupport() {
    return this.$dataService.getters.isOfflineSupport();
  }

  @Provide()
  messageInfoService = new MessageInfoService();

  @Watch("offlineSupport")
  onOfflineSupportChange(v: boolean | undefined | null) {
    if (v) this.onLogin();
    else this.$dataService.actions.reset();
  }

  @Watch("offLineStatus")
  async onOfflineStatusChange(status: boolean) {
    if (!status) {
      if (!this.userInfo || !this.userInfo.expires || !this.userInfo.refreshToken)
        return await this.logoutActions();
      const isExpired = isBefore(new Date(this.userInfo.expires), new Date());
      if (isExpired) {
        this.$dataService.actions.TimeOut.stop(true);
        this.loginDialog = true;
        return;
      } else {
        const { data } = await this.$dataService.actions.Auth.refreshToken(
          this.userInfo.refreshToken
        );
        if (data) {
          this.$dataService.actions.Auth.putUserInfo(data);
          this.showNotificationRunAllDialog = this.$dataService.getters.FuncQueue.isNotEmpty();
          return;
        }
      }
    }
  }

  rules = {
    required: (v: any) => !!v || "This field is required,"
  };
  private timer: any | null = null;

  async created() {
    const platform = (await Device.getInfo())?.platform;
    if (
      window.matchMedia("(display-mode: standalone)").matches || // WPA
      platform != "web" || // from capacitor - iOS and Android and capacitor Electron
      navigator.userAgent.toLowerCase().includes("electron") // for electron (not from capacitor)
    )
      document.body.classList.add("hide-scrollbar");
    console.info("platform: " + platform);
    document.addEventListener("logOut", this.logoutActions);
    document.addEventListener("sessionTimeOut", this.sessionTimeOutEvent);
    document.addEventListener("click", () => this.$dataService.actions.TimeOut.reset(), {
      passive: true
    });
    window.addEventListener("beforeinstallprompt", e => {
      this.deferredPrompt = e;
      this.showInstallAppButton = true;
      console.info("beforeinstallprompt Event Fired!");
    });
    window.addEventListener("online", this.$dataService.actions.setOnline);
    window.addEventListener("offline", this.$dataService.actions.setOffline);
    document.addEventListener("swUpdated", e => {
      this.showUpdateMsg = true;
    });
  }

  mounted() {
    this.userInfo = this.$dataService.actions.Auth.getUserInfo();
  }

  sessionTimeOutEvent(e: CustomEventInit<SessionTimeoutInfo>) {
    if (!e.detail) return;
    this.elapsedTimeProgress = e.detail.elapsedTimePerCent;
    this.elapsedTimeDisplay = e.detail.ToString();
  }

  checkMenu() {
    this.drawer = true;
    if (this.drawer === null) {
      this.miniMenu = false;
      return;
    } else if (this.$vuetify.breakpoint.lgAndUp) {
      this.miniMenu = !this.miniMenu;
    }
  }

  cancelMessage() {
    this.commentsMessage = null;
    this.subjectMessage = null;
    this.sendMsgDialog = false;
  }
  fromLoginDialog() {
    this.loginDialog = false;
    this.showNotificationRunAllDialog = this.$dataService.getters.FuncQueue.isNotEmpty();
  }

  async sendMessage() {
    if (!this.subjectMessage || !this.commentsMessage) return;
    this.messageSending = true;
    try {
      await this.messageInfoService.send({
        subject: this.subjectMessage,
        message: this.commentsMessage
      });
      this.$dataService.actions.showMessage("success", "Message Sent successfully!");
    } catch (error: any) {
      this.$dataService.actions.showErrorExceptionMessage(error);
    } finally {
      this.sendMsgDialog = false;
      this.messageSending = false;
      this.$refs.form.reset();
    }
  }

  logout() {
    document.dispatchEvent(new Event("logOut"));
  }

  async logoutActions() {
    try {
      this.$dataService.actions.Auth.removeUserInfo();
      this.userInfo = null;
      this.$dataService.actions.TimeOut.stop();
      await this.$router.replace("/login");
    } catch (e: any) {
      if (e && e.name && e.name === "NavigationDuplicated") return;
      this.$dataService.actions.showErrorExceptionMessage(e);
    }
  }

  async onLogin() {
    try {
      this.userInfo = this.$dataService.actions.Auth.getUserInfo();
      if (!this.$dataService.getters.isOfflineSupport()) return;
      this.isInitialize = true;
      await this.$dataService.initialize();
    } catch (e: any) {
      this.$dataService.actions.showErrorExceptionMessage(e);
    } finally {
      this.isInitialize = false;
    }
  }

  swUpdatedFound(e: Event | undefined) {
    this.swSnackBarMode = true;
    this.swText = this.swUpdatedFoundText;
    this.showProgress = true;
    this.timer = setTimeout(() => {
      this.swUpdated(undefined);
    }, 1000);
  }

  async installApp() {
    if (this.deferredPrompt == null) return;
    await this.deferredPrompt.prompt();
    this.deferredPrompt = null;
    this.showInstallAppButton = false;
  }

  swUpdated(e: Event | undefined) {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.swSnackBarMode = true;
    this.swText = this.swUpdatedText;
    this.showProgress = false;
  }

  async refreshWindow() {
    await this.$dataService.actions.reset();
    setTimeout(() => window.location.reload());
  }
}
